import { logger } from '@/core/logger';
import { Platform } from 'react-native';

// Branch.io only works on native platforms (iOS/Android), not web
// Use conditional import to prevent web bundling errors
const isNative = Platform.OS !== 'web';
let branch: any;
let BranchEvent: any;

if (isNative) {
    try {
        const branchModule = require('react-native-branch');
        branch = branchModule.default || branchModule;
        BranchEvent = branchModule.BranchEvent;
        // Deep linking only: no cross-app attribution / ad ID (Branch consumer protection API).
        // Call as early as possible after the native module loads (before subscribe / session use).
        if (typeof branch?.setConsumerProtectionAttributionLevel === 'function') {
            branch.setConsumerProtectionAttributionLevel('NONE');
            logger.info(
                'BranchService',
                'Branch attribution level set to NONE (deep linking only; no ad/attribution tracking)'
            );
        }
    } catch (error) {
        // react-native-branch not available (shouldn't happen on native, but handle gracefully)
        logger.warn('BranchService', 'react-native-branch module not available');
    }
}

/**
 * Branch.io — deep linking only (no ad attribution / cross-app tracking).
 * Uses setConsumerProtectionAttributionLevel('NONE') at load time.
 * Only works on native platforms (iOS/Android), not web.
 */
class BranchService {
  private isInitialized = false;
  private unsubscribe: (() => void) | null = null;

  /**
   * Initialize Branch SDK
   * In Expo apps, Branch is already initialized by the config plugin natively.
   * We just need to subscribe to events.
   */
  async initialize(): Promise<void> {
    if (Platform.OS === 'web') {
      logger.info('BranchService', 'Branch not supported on web platform');
      return;
    }

    if (this.isInitialized) {
      logger.warn('BranchService', 'Branch already initialized');
      return;
    }

    if (!branch) {
      logger.warn('BranchService', 'Branch module not available');
      return;
    }

    try {
      if (typeof branch.setConsumerProtectionAttributionLevel === 'function') {
        branch.setConsumerProtectionAttributionLevel('NONE');
      }

      // Subscribe to Branch events
      // Note: In Expo, Branch is already initialized natively by the config plugin
      // We just subscribe to events here
      // branch.subscribe() returns an unsubscribe function
      this.unsubscribe = branch.subscribe(({ error, params, uri }: { error?: any; params?: any; uri?: string }) => {
        if (error) {
          // Filter out the "already initialized" warning - this is expected in Expo
          let errorMessage = '';
          if (typeof error === 'string') {
            errorMessage = error;
          } else if (error && typeof error === 'object' && 'message' in error) {
            errorMessage = String((error as any).message);
          } else {
            errorMessage = String(error);
          }
          
          if (errorMessage.includes('Session initialization already happened')) {
            // This is expected - Branch is initialized by Expo config plugin
            logger.debug('BranchService', 'Branch session already initialized (expected in Expo)');
            return;
          }
          
          logger.error('BranchService', 'Branch error', error);
          return;
        }

        if (params && !params['+clicked_branch_link']) {
          // Not a Branch link
          return;
        }

        // Handle Branch link
        logger.info('BranchService', 'Branch link opened', {
          uri,
          params: JSON.stringify(params),
        });

        // TODO: Handle deep link routing based on params
        // Example: if (params['$deeplink_path']) { router.push(params['$deeplink_path']) }
      });

      this.isInitialized = true;
      logger.info('BranchService', 'Branch event subscription initialized');
    } catch (error) {
      logger.error('BranchService', 'Failed to subscribe to Branch events', error);
      throw error;
    }
  }

  /**
   * Create a Branch link
   * @param data - Branch link data
   * @returns Branch link URL
   */
  async createLink(data: {
    [key: string]: any;
  }): Promise<string | null> {
    if (Platform.OS === 'web' || !this.isInitialized) {
      logger.warn('BranchService', 'Branch not available for link creation');
      return null;
    }

    if (!branch) {
      logger.warn('BranchService', 'Branch module not available for link creation');
      return null;
    }

    try {
      // Create Branch Universal Object first
      const buo = await branch.createBranchUniversalObject('', {
        locallyIndex: false,
        title: data.title || 'Shared Link',
        contentDescription: data.description || '',
        contentMetadata: {
          customMetadata: data,
        },
      });

      // Generate short link
      const linkProperties = {
        feature: data.feature || 'sharing',
        channel: data.channel || 'app',
      };

      const { url } = await buo.generateShortUrl(linkProperties);
      logger.info('BranchService', 'Branch link created', { url });
      return url;
    } catch (error) {
      logger.error('BranchService', 'Failed to create Branch link', error);
      return null;
    }
  }

  /**
   * Track Branch event
   * @param event - Branch event name
   * @param data - Event data (optional custom metadata)
   */
  async trackEvent(event: string, data?: { [key: string]: any }): Promise<void> {
    if (Platform.OS === 'web' || !this.isInitialized) {
      logger.warn('BranchService', 'Branch not available for event tracking');
      return;
    }

    if (!branch || !BranchEvent) {
      logger.warn('BranchService', 'Branch module not available for event tracking');
      return;
    }

    try {
      const branchEvent = new BranchEvent(event);
      if (data && branchEvent.customData) {
        // Add custom data to the event
        Object.keys(data).forEach((key) => {
          branchEvent.customData![key] = data[key];
        });
      }
      await branchEvent.logEvent();
      logger.info('BranchService', 'Branch event tracked', { event, data });
    } catch (error) {
      logger.error('BranchService', 'Failed to track Branch event', error);
    }
  }

  /**
   * Get latest Branch params (for deep linking)
   * @returns Branch params or null
   */
  async getLatestParams(): Promise<any | null> {
    if (Platform.OS === 'web' || !this.isInitialized) {
      return null;
    }

    if (!branch) {
      return null;
    }

    try {
      const params = await branch.getFirstReferringParams();
      return params;
    } catch (error) {
      logger.error('BranchService', 'Failed to get Branch params', error);
      return null;
    }
  }

  /**
   * Cleanup Branch subscriber
   * branch.subscribe() returns an unsubscribe function that should be called to clean up
   * This method properly calls the unsubscribe function to remove the event listener
   */
  cleanup(): void {
    if (this.unsubscribe) {
      try {
        // IMPORTANT: Call the unsubscribe function to properly remove the event listener
        // This prevents memory leaks and ensures the subscription is fully cleaned up
        this.unsubscribe();
        this.unsubscribe = null;
        this.isInitialized = false;
        logger.info('BranchService', 'Branch subscription cleaned up');
      } catch (error) {
        logger.error('BranchService', 'Error during Branch cleanup', error);
        // Reset state even if unsubscribe fails
        this.unsubscribe = null;
        this.isInitialized = false;
      }
    }
  }
}

export const branchService = new BranchService();

